import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import moment from 'moment';
import { Box, Flex } from 'UIKit/Grid';
import SourceLink from '@/components/SourceLink';
import { colors } from 'sama_global/theme';
import { countries } from 'sama_global/modules/olimpic';
import Table from '../Table';

const countryMap = new Map(
  countries.map(({ value, image }) => [value, { value, image }])
);

export const places = {
  '-1': 'CHAMPION_OF_CHAMPIONS',
  0: 'CHAMPION',
  1: 'place1',
  2: 'place2',
  3: 'place3',
};

function OlimpicTable({
  data,
  intl,
  title,
  options,
  result,
  personal,
  components,
}) {
  const columns = useMemo(
    () => [
      {
        name: '_id',
        label: 'id',
        options: {
          filter: false,
          searchable: false,
          display: 'excluded',
          sort: false,
        },
      },
      {
        name: 'user',
        label: intl.formatMessage({ id: 'NAME' }),
        options: {
          filter: false,
          sortThirdClickReset: true,
          display: !personal,
        },
      },
      {
        name: 'place',
        label: intl.formatMessage({ id: 'PLACE' }),
        options: {
          filter: false,
          searchable: false,
          display: result,
          customBodyRender: value =>
            intl.formatMessage({
              id: (value > 3 ? 'COMPETITOR' : places[value]) || ' ',
            }),
        },
      },
      {
        name: 'link',
        label: intl.formatMessage({ id: 'LINK_BY_VIDEO' }),
        options: {
          customBodyRender: value =>
            value && <SourceLink link={value} background={colors.gray} />,
          filter: false,
          searchable: false,
          display: result,
          sort: false,
        },
      },
      {
        name: 'date',
        label: intl.formatMessage({ id: 'DATE' }),
        options: {
          filter: false,
          sort: true,
          display: personal,
          sortThirdClickReset: true,
          customBodyRender: value => moment(value).format('DD.MM.YYYY'),
        },
      },
      {
        name: 'category',
        label: intl.formatMessage({ id: 'CATEGORY' }),
        options: {
          sortThirdClickReset: true,
          display: !personal,
        },
      },
      {
        name: 'country',
        label: intl.formatMessage({ id: 'COUNTRY' }),
        options: {
          sortThirdClickReset: true,
          display: !personal,
          customBodyRender: value => {
            const country = countryMap.get(value);

            return (
              <Flex alignItems="center">
                {country ? (
                  <>
                    <img
                      src={country && country.image}
                      alt=""
                      style={{ width: 30, borderRadius: '100%' }}
                    />
                    <Box ml={2}>
                      {intl.formatMessage({ id: `country.${value}` })}
                    </Box>
                  </>
                ) : (
                  <>{value}</>
                )}
              </Flex>
            );
          },
        },
      },
      ...[1, 2, 3, 4, 5, 6, 7].map(item => ({
        name: result ? 'result' : 'tryout',
        label: intl.formatMessage({ id: 'stage_num' }, { stage: item }),
        options: {
          customBodyRender: value => value && value[item - 1],
          filter: false,
          sort: item === 7,
          sortThirdClickReset: true,
          searchable: false,
        },
      })),
      {
        name: result ? 'score' : 'max',
        label: intl.formatMessage({
          id: 'OVERALL_SCORE',
        }),
        options: {
          filter: false,
          sortThirdClickReset: true,
        },
      },
    ],
    [intl, options]
  );

  return (
    <Table
      columns={columns}
      data={data}
      title={title}
      options={{
        selectableRows: 'none',
        customSort: (d, colIndex, order) => {
          const defaultCompare = (a, b) =>
            a.data[colIndex]?.localeCompare
              ? a.data[colIndex].localeCompare(b.data[colIndex])
              : a.data[colIndex] - b.data[colIndex];

          const step = columns.length - 2;
          const score = columns.length - 1;

          const nestedCompare = (a, b) => {
            if (a.data[score] > b.data[score]) return 1;

            if (
              a.data[step]?.[6] > b.data[step]?.[6] &&
              a.data[score] === b.data[score]
            )
              return 1;

            return -1;
          };

          return d.sort(
            (a, b) =>
              (colIndex >= step ? nestedCompare : defaultCompare)(a, b) *
              (order === 'desc' ? 1 : -1)
          );
        },
        ...options,
      }}
      components={components}
    />
  );
}

OlimpicTable.propTypes = {
  title: PropTypes.string,
  data: PropTypes.array,
  intl: PropTypes.object.isRequired,
  options: PropTypes.object,
  result: PropTypes.bool,
  personal: PropTypes.bool,
  components: PropTypes.object,
};

OlimpicTable.defaultProps = {
  data: [],
  title: '',
  options: {},
  result: false,
  personal: false,
  components: {},
};

export default connect()(injectIntl(OlimpicTable));
